import { useState, useEffect, useCallback, useRef } from "react";

type LongPressOptions = {
  threshold?: number;
};

type LongPressEvent = MouseEvent | TouchEvent;

const useLongPress = (
  onLongPress: (e: LongPressEvent) => void,
  onClick: (e: LongPressEvent) => void,
  onLongPressEnd?: (e: LongPressEvent) => void,
  { threshold = 300 }: LongPressOptions = {}
) => {
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const targetRef = useRef<HTMLElement | null>(null);

  const start = useCallback(
    (e: LongPressEvent) => {
      e.preventDefault();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        onLongPress(e);
        setLongPressTriggered(true);
      }, threshold);
    },
    [onLongPress, threshold]
  );

  const clear = useCallback(
    (e: LongPressEvent, shouldTriggerClick = true) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (shouldTriggerClick && !longPressTriggered) {
        onClick(e);
      }
      if (longPressTriggered && onLongPressEnd) {
        onLongPressEnd(e);
      }
      setLongPressTriggered(false);
    },
    [onClick, onLongPressEnd, longPressTriggered]
  );

  useEffect(() => {
    const element = targetRef.current;
    if (!element) return;

    const handleMouseDown = (e: MouseEvent) => start(e);
    const handleMouseUp = (e: MouseEvent) => clear(e);
    const handleMouseLeave = (e: MouseEvent) => clear(e, false);
    const handleTouchStart = (e: TouchEvent) => start(e);
    const handleTouchEnd = (e: TouchEvent) => clear(e);

    element.addEventListener("mousedown", handleMouseDown);
    element.addEventListener("mouseup", handleMouseUp);
    element.addEventListener("mouseleave", handleMouseLeave);
    element.addEventListener("touchstart", handleTouchStart);
    element.addEventListener("touchend", handleTouchEnd);

    return () => {
      element.removeEventListener("mousedown", handleMouseDown);
      element.removeEventListener("mouseup", handleMouseUp);
      element.removeEventListener("mouseleave", handleMouseLeave);
      element.removeEventListener("touchstart", handleTouchStart);
      element.removeEventListener("touchend", handleTouchEnd);
    };
  }, [start, clear]);

  return targetRef;
};

export default useLongPress;
